import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_northern-lights-glow/artifacts/m9am00yr_6FCF8E66-33C7-4E29-A59C-D19380935074.PNG";
const KANDI_PHOTO = "https://customer-assets.emergentagent.com/job_northern-lights-glow/artifacts/b937rm29_image.png";

const AboutPage = () => {
  return (
    <>
      <SEO 
        title="About Kandi - Licensed Esthetician"
        description="Meet Kandi, Licensed Esthetician and Certified Body Sculpting Artist. Founder of Glamour Body in Katy, Texas since June 2024. Expert in advanced skincare, body contouring, and personalized beauty treatments."
        keywords="Kandi esthetician Katy Texas, licensed esthetician Houston, body sculpting artist, Glamour Body founder, skincare expert Katy TX, certified body contouring specialist"
        canonical="https://glamourbodyhouston.com/about"
      />
      
      <div className="pt-24 min-h-screen">
        <section className="relative py-16 px-4" data-testid="about-section">
          <div className="orb-cyan top-20 -left-40" />
          <div className="orb-purple bottom-40 -right-60" />
          <div className="orb-green top-60 right-20" />
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="relative">
                  <div className="aurora-border rounded-2xl overflow-hidden p-1">
                    <img 
                      src={LOGO_URL} 
                      alt="Glamour Body Logo" 
                      className="w-full rounded-xl bg-black"
                    />
                  </div>
                  <div className="mt-8 flex justify-center">
                    <div className="aurora-border rounded-full p-1">
                      <img 
                        src={KANDI_PHOTO} 
                        alt="Kandi - Licensed Esthetician & Founder of Glamour Body" 
                        className="w-40 h-40 rounded-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div className="absolute -bottom-6 -right-6 glass-card rounded-xl p-4 aurora-glow">
                    <p className="font-syne text-cyan-400">Est. June 2024</p>
                    <p className="text-white/70 text-sm">Katy, Texas</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h1 className="font-italiana text-4xl sm:text-5xl text-white mb-2 tracking-wider uppercase">Meet Kandi</h1>
                <p className="gradient-text font-syne text-lg mb-6">Licensed Esthetician & Certified Body Sculpting Artist</p>
                
                <div className="space-y-4 text-white/70">
                  <p>
                    Kandi is a Licensed Esthetician and Certified Body Sculpting Artist with a passion for helping clients look and feel their absolute best. As the proud Founder of Glamour Body, she brings years of expertise in body contouring and advanced skincare treatments to the Katy, Texas community.
                  </p>
                  <p>
                    Since founding Glamour Body in June 2024, Kandi has been dedicated to delivering personalized, results-driven services designed to empower clients and enhance their natural beauty. Her mission is to create a welcoming space where beauty meets wellness—ensuring every client's experience is customized to their unique goals.
                  </p>
                  <p>
                    Committed to staying at the forefront of industry trends and techniques, Kandi continuously expands her knowledge and skills to offer the latest innovations in beauty and body care. At Glamour Body, you can trust you're in the hands of a professional who genuinely cares about your transformation and confidence journey.
                  </p>
                </div>
                
                <div className="mt-8 glass-card rounded-xl p-6">
                  <p className="font-italiana text-xl text-white italic">"Experience the Glamour Body difference — where confidence begins."</p>
                </div>
                
                <div className="mt-8">
                  <Link to="/contact">
                    <Button className="btn-primary rounded-full px-8 py-4">
                      Book Your Appointment
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
            
            {/* Credentials & Expertise */}
            <motion.div 
              className="mt-24"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <h2 className="font-italiana text-3xl text-white text-center mb-12">Expertise & Certifications</h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="glass-card rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 flex items-center justify-center">
                    <span className="text-2xl">🎓</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Licensed Esthetician</h3>
                  <p className="text-white/60 text-sm">State licensed professional with comprehensive training in skincare treatments</p>
                </div>
                
                <div className="glass-card rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                    <span className="text-2xl">💎</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Certified Body Sculpting Artist</h3>
                  <p className="text-white/60 text-sm">Expert in cavitation, wood therapy, and advanced body contouring techniques</p>
                </div>
                
                <div className="glass-card rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-500/20 to-emerald-500/20 flex items-center justify-center">
                    <span className="text-2xl">✨</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Advanced Skincare Specialist</h3>
                  <p className="text-white/60 text-sm">Trained in LED therapy, RF treatments, and hydro jelly facials</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;
